// Which studies a patient is enrolled in, and when they go back to referrals.
//
// A patient record can carry `enrolledStudies`, a list of the studies they are
// enrolled in. It lives on the consents/duplicates doc for Firestore patients
// and inside CLINICAL_DATA for legacy Sheet patients. Each entry:
//
//   { studyId, studyName, disease, source: "coordinator" | "backfill",
//     enrolledAt, enrolledBy, approval: "pending" | "approved" | "rejected",
//     approvedAt/By?, rejectedAt/By/Reason?,
//     matchScore?, matchReasons?, verifiedAt?, verifiedBy?,
//     closedAt?, returnedAt?, returnedReason?, dismissedAt?, dismissedBy? }
//
// Entries are never removed when a study closes or the patient moves on. They
// are the permanent record that the patient was in that study. An entry that
// turns out to be wrong (a mis-picked study, a backfill match that isn't this
// patient) is DISMISSED, and one an admin turns down is REJECTED. Neither is
// deleted: they stay on the record with who/when, and every rule below
// ignores them.
//
// THE RULE: a patient goes back to Lung Center Referrals only when EVERY study
// they are in is either a Diagnostics study or closed. One non-diagnostics
// study that is still open keeps them Enrolled. The patient page, the
// study-close pop-up and the backfill script all use shouldReturnToReferrals,
// so the rule can't drift between them.
//
// Deliberately import-free and Firebase-free: Jest tests it directly, and the
// backfill script (plain Node) imports it without a bundler.

export const ENROLLED_STATUS = "Enrolled";
export const REFERRALS_STATUS = "Lung Center Referrals";

const DIAGNOSTICS = "diagnostics";
const CLOSED_STATUSES = new Set(["CLOSED", "CLOSED/CLOSING"]);
const ENROLLABLE_STATUSES = new Set(["ACTIVE", "ACTIVE/NR"]);

const statusKey = (s) => (s || "").toString().trim().toUpperCase();

// DISEASE is a single string on today's studies. An array is tolerated in case
// an older or hand-edited record holds one.
const diseaseValues = (disease) =>
  (Array.isArray(disease) ? disease : [disease])
    .map((d) => (d || "").toString().trim().toLowerCase())
    .filter(Boolean);

export const isDiagnosticsDisease = (disease) =>
  diseaseValues(disease).includes(DIAGNOSTICS);

export const isDiagnosticsStudy = (study) =>
  !!study && isDiagnosticsDisease(study.DISEASE);

export const isClosedStudy = (study) =>
  !!study && CLOSED_STATUSES.has(statusKey(study.STATUS));

// The studies a coordinator can pick when marking a patient Enrolled.
export const isEnrollableStudy = (study) =>
  !!study && ENROLLABLE_STATUSES.has(statusKey(study.STATUS));

export const isEnrolledStatus = (status) =>
  (status || "").toString().trim().toLowerCase() === ENROLLED_STATUS.toLowerCase();

// A study's display name, the same "NAME" fallback the rest of the app uses.
export const studyDisplayName = (study) =>
  (study && (study.NAME || study.name || study.STUDY_ID || study.id)) || "";

// Read `enrolledStudies` from any record shape. Sheet rows can carry it as a
// JSON string if CLINICAL_DATA was ever hand-edited, so a string is parsed.
// Anything unreadable yields [] rather than throwing into a page load.
export const normalizeEnrolledStudies = (raw) => {
  let list = raw;
  if (typeof list === "string") {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list.filter(
    (e) => e && typeof e === "object" && e.studyId !== undefined && e.studyId !== null && e.studyId !== ""
  );
};

// Dismissed by staff on the patient page, or rejected by an admin at
// approval. Either way the patient isn't in that study, so it stops counting,
// but the entry stays on the record.
export const isDismissed = (entry) =>
  !!(entry && (entry.dismissedAt || entry.approval === "rejected"));

// The entries that count: everything except dismissed ones.
export const activeEnrolledStudies = (raw) =>
  normalizeEnrolledStudies(raw).filter((e) => !isDismissed(e));

// Is this one enrollment "done",meaning it no longer keeps the patient
// Enrolled? True if the study is diagnostics or closed. A closedAt stamp on the
// entry counts as closed even if the study was later reopened or deleted, and
// the disease snapshot taken at pick time covers a study that no longer exists.
// An entry whose study can't be found and carries no snapshot is NOT done, so
// missing data keeps the patient where they are.
export const isEntryDone = (entry, study) => {
  if (!entry) return false;
  if (entry.closedAt) return true;
  if (study) return isDiagnosticsStudy(study) || isClosedStudy(study);
  return isDiagnosticsDisease(entry.disease);
};

const lookup = (studiesById, id) => {
  if (!studiesById) return undefined;
  if (studiesById instanceof Map) return studiesById.get(id);
  return studiesById[id];
};

/**
 * Should this patient go back to Lung Center Referrals?
 *
 * @param {Array<object>} entries - the patient's enrolledStudies.
 * @param {Map|object} studiesById - study docs keyed by id (DISEASE, STATUS).
 * @returns {{ move: boolean, reason: "diagnostics"|"closed"|"diagnostics_and_closed"|null,
 *            keeping: Array<object> }} `keeping` lists the entries that hold
 *            the patient in Enrolled, so the UI can say which study.
 */
export const shouldReturnToReferrals = (entries, studiesById) => {
  const list = activeEnrolledStudies(entries);
  if (!list.length) return { move: false, reason: null, keeping: [] };

  const keeping = [];
  let anyClosed = false;
  let anyDiagnostics = false;
  for (const entry of list) {
    const study = lookup(studiesById, entry.studyId);
    if (!isEntryDone(entry, study)) {
      keeping.push(entry);
      continue;
    }
    const closed = !!entry.closedAt || isClosedStudy(study);
    if (closed) anyClosed = true;
    else anyDiagnostics = true;
  }

  if (keeping.length) return { move: false, reason: null, keeping };
  const reason =
    anyClosed && anyDiagnostics ? "diagnostics_and_closed" : anyClosed ? "closed" : "diagnostics";
  return { move: true, reason, keeping: [] };
};

export const RETURN_REASON_TEXT = {
  diagnostics: "All of this patient's studies are diagnostics studies",
  closed: "All of this patient's studies have closed",
  diagnostics_and_closed: "All of this patient's studies are diagnostics studies or have closed",
};

/**
 * Stamp the entries when a patient is returned to referrals. Returns a NEW
 * array; the input is never mutated. Existing stamps are kept, never
 * overwritten, so re-running is harmless and the first date stays true.
 */
export const stampReturn = (entries, studiesById, { at, by, reason } = {}) =>
  normalizeEnrolledStudies(entries).map((entry) => {
    if (isDismissed(entry)) return entry;
    const study = lookup(studiesById, entry.studyId);
    const next = { ...entry };
    if (!next.closedAt && isClosedStudy(study)) next.closedAt = at || null;
    if (!next.returnedAt) {
      next.returnedAt = at || null;
      next.returnedBy = by || null;
      next.returnedReason = reason || null;
    }
    return next;
  });

/**
 * Add enrollments without ever replacing one that is already there. Keyed by
 * studyId, and the existing entry wins, so its dates, approval and any
 * verification survive a re-pick or a backfill re-run. A dismissed entry does
 * not block the same study being added again; both stay on the record.
 */
export const mergeEnrolledStudies = (existing, additions) => {
  const base = normalizeEnrolledStudies(existing);
  const seen = new Set(base.filter((e) => !isDismissed(e)).map((e) => String(e.studyId)));
  const out = [...base];
  for (const entry of normalizeEnrolledStudies(additions)) {
    const key = String(entry.studyId);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
};

/**
 * The enrollment entries for studies a coordinator just picked (the patient
 * page's Enrolled pop-up, and adding a patient to RegX from a weekly report).
 * Each goes to an admin for approval onto the study list.
 */
export const coordinatorPickEntries = (picks, { at, by } = {}) =>
  (picks || []).map((s) => ({
    studyId: s.id,
    studyName: studyDisplayName(s),
    disease: s.DISEASE || "",
    source: "coordinator",
    enrolledAt: at,
    enrolledBy: by,
    approval: "pending",
  }));

const FINAL_APPROVALS = new Set(["approved", "rejected"]);

// Identity of one enrollment entry. studyId alone isn't enough, because a
// dismissed entry and a later re-add of the same study both stay on the record.
export const entryKey = (e) =>
  `${e && e.studyId}|${(e && e.enrolledAt) || ""}|${(e && e.source) || ""}`;

/**
 * Combine the copy of `enrolledStudies` this page edited (`local`) with the
 * copy stored right now (`fresh`), so a save never erases a change someone
 * else made after the page loaded (an admin closing a study, the backfill
 * adding a match). Every edit to an entry only ADDS fields (dismissedAt,
 * verifiedAt, returnedAt, approval), so merging field-by-field loses nothing.
 * Entries found in only one copy are kept.
 */
export const mergeEntryUpdates = (fresh, local) => {
  const out = [];
  const index = new Map();
  for (const e of normalizeEnrolledStudies(fresh)) {
    index.set(entryKey(e), out.length);
    out.push({ ...e });
  }
  for (const e of normalizeEnrolledStudies(local)) {
    const k = entryKey(e);
    if (index.has(k)) {
      const i = index.get(k);
      // An admin's decision ("approved" or "rejected") is final. A page that
      // loaded while the entry was still pending must not flip it back.
      const decided = FINAL_APPROVALS.has(out[i].approval) ? out[i].approval : null;
      out[i] = { ...out[i], ...e };
      if (decided) out[i].approval = decided;
    } else {
      index.set(k, out.length);
      out.push({ ...e });
    }
  }
  return out;
};

// Which studyIds were added and removed (dismissed) between two lists, for the
// audit trail.
export const diffEnrolledStudies = (prev, next) => {
  const a = activeEnrolledStudies(prev);
  const b = activeEnrolledStudies(next);
  const aIds = new Set(a.map((e) => String(e.studyId)));
  const bIds = new Set(b.map((e) => String(e.studyId)));
  return {
    added: b.filter((e) => !aIds.has(String(e.studyId))),
    removed: a.filter((e) => !bIds.has(String(e.studyId))),
  };
};

// Studies the patient is in now (not closed) vs. in the past (closed).
export const splitCurrentAndPast = (entries, studiesById) => {
  const current = [];
  const past = [];
  for (const entry of activeEnrolledStudies(entries)) {
    const study = lookup(studiesById, entry.studyId);
    if (entry.closedAt || isClosedStudy(study)) past.push(entry);
    else current.push(entry);
  }
  return { current, past };
};

// Stamp closedAt on this patient's (non-dismissed) entries for one study,
// keeping any date already there. Returns a new array.
export const stampStudyClosed = (entries, studyId, at) =>
  normalizeEnrolledStudies(entries).map((e) =>
    !isDismissed(e) && String(e.studyId) === String(studyId) && !e.closedAt
      ? { ...e, closedAt: at || null }
      : e
  );

/**
 * What closing a study means for each patient. Pure: the caller loads the
 * patients and studies, and applies the plan.
 *
 * Only patients whose status is Enrolled and who hold a current entry for the
 * study are affected. Each gets the study stamped closed; those whose every
 * remaining study is now diagnostics or closed also go back to referrals.
 *
 * @param {Array<{id, status, enrolledStudies}>} patients
 * @param {object} study - the study being closed (id, NAME, ...)
 * @param {Array<object>} allStudies - every study at the site
 * @param {{at: string, by: string}} stamp
 * @returns {Array<{patient, move: boolean, reason, keeping: Array, nextEntries: Array}>}
 */
export const planStudyClose = (patients, study, allStudies, { at, by } = {}) => {
  if (!study) return [];
  const studiesById = new Map((allStudies || []).map((s) => [s.id, s]));
  // Treat the study as closed even if the caller's copy predates the save.
  studiesById.set(study.id, { ...(studiesById.get(study.id) || {}), ...study, STATUS: "CLOSED" });

  const plans = [];
  for (const patient of patients || []) {
    if (!patient || !isEnrolledStatus(patient.status)) continue;
    const entries = normalizeEnrolledStudies(patient.enrolledStudies);
    const inStudy = entries.some(
      (e) => !isDismissed(e) && !e.closedAt && String(e.studyId) === String(study.id)
    );
    if (!inStudy) continue;

    let nextEntries = stampStudyClosed(entries, study.id, at);
    const outcome = shouldReturnToReferrals(nextEntries, studiesById);
    // Same guard as the backfill and Verify: a match under 90% that nobody
    // has verified can't be what frees a patient. They stay Enrolled (the
    // close is still recorded) until staff verify or dismiss it.
    const unsure = unverifiedLowMatches(nextEntries);
    const move = outcome.move && unsure.length === 0;
    if (move) {
      nextEntries = stampReturn(nextEntries, studiesById, { at, by, reason: outcome.reason });
    }
    plans.push({
      patient,
      move,
      reason: move ? outcome.reason : null,
      keeping: move ? [] : outcome.move ? unsure : outcome.keeping,
      unsure: outcome.move && unsure.length > 0,
      nextEntries,
    });
  }
  return plans;
};

/**
 * The diagnostics studies a patient is in right now, for the DIAGNOSTICS
 * badge. Derived from `enrolledStudies`, so the badge follows the record: it
 * goes away when that study closes, is dismissed, or is rejected.
 *
 * The stored `enrolledInDiagnostics` flag is no longer read for this, with one
 * exception: a patient flagged by the old Yes/No pop-up, which recorded no
 * study. Those have no diagnostics entry at all (current, past or dismissed),
 * so the flag is the only record and still shows, labelled without a study.
 *
 * @returns {{ show: boolean, studies: Array<object>, legacy: boolean }}
 */
export const diagnosticsBadgeFor = (patient, studiesById) => {
  const all = normalizeEnrolledStudies(patient && patient.enrolledStudies);
  const isDiagEntry = (e) => {
    const s = lookup(studiesById, e.studyId);
    return s ? isDiagnosticsStudy(s) : isDiagnosticsDisease(e.disease);
  };
  const studies = splitCurrentAndPast(all, studiesById).current.filter(isDiagEntry);
  if (studies.length) return { show: true, studies, legacy: false };

  const raw = patient && patient.enrolledInDiagnostics;
  const flagged = raw === true || String(raw).toLowerCase() === "true";
  const legacy = flagged && !all.some(isDiagEntry);
  return { show: legacy, studies: [], legacy };
};

// Scores are only a starting aid for the backfill. Once staff verify an entry,
// or when it was picked by a coordinator, there is no score to show.
export const hasUnverifiedScore = (entry) =>
  !!entry &&
  typeof entry.matchScore === "number" &&
  !entry.verifiedAt;

// The auto-move threshold the backfill uses: 90% and up.
export const AUTO_MOVE_SCORE = 90;

// Backfill matches under 90% that nobody has verified. While any exist, no
// path may move the patient to referrals: the study picker, Verify / the Move
// notice, study close and the backfill all check this.
export const unverifiedLowMatches = (entries) =>
  activeEnrolledStudies(entries).filter(
    (e) => hasUnverifiedScore(e) && e.matchScore < AUTO_MOVE_SCORE
  );
